import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import type { StatusId } from "#shared/constants.ts";
import { BulkResultSchema } from "#shared/schemas/bulk.ts";
import {
  EntityDetailResponseSchema,
  type DetailGenre,
  type DetailSuggestedGenre,
  type EntityDetail,
} from "#shared/schemas/entityDetail.ts";
import type { FieldValueData } from "#shared/schemas/fieldValue.ts";
import { notifyError } from "@web/features/diagnostics/notifyError.ts";
import { api } from "@web/lib/api.ts";
import type { DetailTarget } from "@web/stores/detail.ts";
import { useUndo } from "@web/features/library/bulkApi.ts";

export function detailKey(target: DetailTarget): QueryKey {
  return ["entity-detail", target.type, target.id];
}

export function useEntityDetail(target: DetailTarget | null) {
  return useQuery({
    queryKey: target ? detailKey(target) : ["entity-detail", "none"],
    enabled: target !== null,
    queryFn: async (): Promise<EntityDetail> => {
      if (!target) throw new Error("No entity selected");
      const body = EntityDetailResponseSchema.parse(
        await api(`/api/entities/${target.type}/${encodeURIComponent(target.id)}`),
      );
      return body.detail;
    },
  });
}

function genreUndoToast(label: string, onUndo: () => void) {
  toast.success(label, {
    action: { label: "Undo", onClick: () => onUndo() },
  });
}

/**
 * Optimistic patch of the cached detail with rollback. Every sheet edit goes
 * through here so a failed write can never leave the panel showing a value the
 * server rejected — v0 lost hand-typed notes exactly that way.
 */
function useDetailMutation<Input>(
  target: DetailTarget,
  options: {
    request: (input: Input) => Promise<unknown>;
    optimistic: (current: EntityDetail, input: Input) => EntityDetail;
    settle?: (body: unknown, current: EntityDetail) => EntityDetail;
    errorMessage: string;
    /** Grid rows show status and field values, so those lists need refetching. */
    invalidateTracks?: boolean;
    onSettledExtra?: (input: Input) => void;
  },
) {
  const qc = useQueryClient();
  const key = detailKey(target);

  return useMutation({
    mutationFn: (input: Input) => options.request(input),
    onMutate: async (input: Input) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<EntityDetail>(key);
      if (previous) qc.setQueryData<EntityDetail>(key, options.optimistic(previous, input));
      return { previous };
    },
    onError: (err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
      notifyError(err, {
        fallbackCode: "SAVE_FAILED",
        actionLabel: options.errorMessage,
      });
    },
    onSuccess: (body) => {
      const settle = options.settle;
      if (!settle) return;
      const current = qc.getQueryData<EntityDetail>(key);
      if (current) qc.setQueryData<EntityDetail>(key, settle(body, current));
    },
    onSettled: (_data, _err, input) => {
      void qc.invalidateQueries({ queryKey: key });
      if (options.invalidateTracks) {
        void qc.invalidateQueries({ queryKey: ["tracks-infinite"] });
      }
      options.onSettledExtra?.(input);
    },
  });
}

export function useSaveFieldValues(target: DetailTarget) {
  const qc = useQueryClient();
  return useDetailMutation<Record<string, FieldValueData>>(target, {
    request: (values) =>
      api(`/api/values/${target.type}/${encodeURIComponent(target.id)}`, {
        method: "PATCH",
        json: values,
      }),
    optimistic: (current, values) => {
      const nextValues = { ...current.values, ...values };
      const countryRaw = nextValues.country;
      const hasCountry = typeof countryRaw === "string" && countryRaw.trim().length > 0;
      return {
        ...current,
        values: nextValues,
        suggested_country:
          current.entity_type === "artist" && hasCountry
            ? null
            : current.suggested_country,
      };
    },
    errorMessage: "Failed to save field",
    invalidateTracks: true,
    onSettledExtra: (input) => {
      if (target.type === "artist" && input && typeof input === "object" && "country" in input) {
        void qc.invalidateQueries({ queryKey: ["globe"] });
      }
    },
  });
}

export interface StatusPatch {
  status?: StatusId;
  follow_up?: string | null;
  drive_url?: string | null;
}

export function useSaveStatus(target: DetailTarget) {
  return useDetailMutation<StatusPatch>(target, {
    request: (patch) =>
      api(`/api/entities/${target.type}/${encodeURIComponent(target.id)}/status`, {
        method: "PATCH",
        json: patch,
      }),
    optimistic: (current, patch) => ({
      ...current,
      status: {
        entity_type: current.entity_type === "genre" ? "track" : current.entity_type,
        entity_id: current.entity_id,
        status: patch.status ?? current.status?.status ?? "none",
        follow_up:
          patch.follow_up === undefined ? (current.status?.follow_up ?? null) : patch.follow_up,
        drive_url:
          patch.drive_url === undefined ? (current.status?.drive_url ?? null) : patch.drive_url,
        board_position: current.status?.board_position ?? null,
        updated_at: new Date().toISOString(),
      },
    }),
    errorMessage: "Failed to save status",
    invalidateTracks: true,
  });
}

export function useSaveTrackBpm(target: DetailTarget) {
  return useDetailMutation<{ bpm: number | null }>(target, {
    request: (input) =>
      api(`/api/entities/${target.type}/${encodeURIComponent(target.id)}/bpm`, {
        method: "PATCH",
        json: input,
      }),
    optimistic: (current, input) => ({
      ...current,
      bpm: input.bpm,
      bpm_manual: input.bpm !== null,
    }),
    settle: (body) => EntityDetailResponseSchema.parse(body).detail,
    errorMessage: "Failed to save BPM",
    invalidateTracks: true,
  });
}

export interface GenreEdit {
  add?: string[];
  remove?: string[];
  /** Chips to show while the write is in flight, since the API echoes ids only. */
  preview?: DetailGenre[];
}

export function useSaveGenres(target: DetailTarget) {
  const undo = useUndo();
  return useDetailMutation<GenreEdit>(target, {
    request: async (edit) => {
      const body = BulkResultSchema.parse(
        await api(`/api/entities/${target.type}/${encodeURIComponent(target.id)}/genres`, {
          method: "POST",
          json: {
            ...(edit.add?.length ? { add: edit.add } : {}),
            ...(edit.remove?.length ? { remove: edit.remove } : {}),
          },
        }),
      );
      genreUndoToast(body.label, () => undo.mutate());
      return body;
    },
    optimistic: (current, edit) => {
      const removed = new Set(edit.remove ?? []);
      const kept = current.genres.filter(
        (g) => !(removed.has(g.genre_id) && g.source === "user"),
      );
      const existing = new Set(kept.map((g) => `${g.genre_id}:${g.source}`));
      const added = (edit.preview ?? []).filter((g) => !existing.has(`${g.genre_id}:${g.source}`));
      return { ...current, genres: [...kept, ...added] };
    },
    errorMessage: "Failed to update genres",
    invalidateTracks: true,
  });
}

const AdoptResultSchema = BulkResultSchema.extend({
  genre_id: z.string(),
  created: z.boolean(),
});

export function useAdoptGenre(target: DetailTarget) {
  const qc = useQueryClient();
  const key = detailKey(target);
  const undo = useUndo();

  return useMutation({
    mutationFn: async (suggestion: DetailSuggestedGenre) =>
      AdoptResultSchema.parse(
        await api(
          `/api/entities/${target.type}/${encodeURIComponent(target.id)}/genres/adopt`,
          {
            method: "POST",
            json: { name: suggestion.name, key: suggestion.key },
          },
        ),
      ),
    onMutate: async (suggestion) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<EntityDetail>(key);
      if (previous) {
        const chip: DetailGenre = {
          genre_id: suggestion.genre_id ?? "gen_pending",
          name: suggestion.name,
          slug: suggestion.key,
          color: null,
          source: "user",
          confidence: null,
        };
        qc.setQueryData<EntityDetail>(key, {
          ...previous,
          genres: [...previous.genres, chip],
          suggested_genres: previous.suggested_genres.filter((s) => s.key !== suggestion.key),
        });
      }
      return { previous };
    },
    onError: (err, _s, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
      notifyError(err, { fallbackCode: "SAVE_FAILED", actionLabel: "adopt genre" });
    },
    onSuccess: (body) => {
      genreUndoToast(body.label, () => undo.mutate());
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ["genres"] });
      void qc.invalidateQueries({ queryKey: ["undo"] });
      void qc.invalidateQueries({ queryKey: ["tracks-infinite"] });
    },
  });
}

export function useDismissGenre(target: DetailTarget) {
  const qc = useQueryClient();
  const key = detailKey(target);
  const undo = useUndo();

  return useMutation({
    mutationFn: async (input: {
      suggestion: DetailSuggestedGenre;
      scope?: "entity" | "global";
    }) =>
      BulkResultSchema.parse(
        await api(
          `/api/entities/${target.type}/${encodeURIComponent(target.id)}/genres/dismiss`,
          {
            method: "POST",
            json: {
              key: input.suggestion.key,
              name: input.suggestion.name,
              scope: input.scope ?? "entity",
            },
          },
        ),
      ),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<EntityDetail>(key);
      if (previous) {
        qc.setQueryData<EntityDetail>(key, {
          ...previous,
          suggested_genres: previous.suggested_genres.filter(
            (s) => s.key !== input.suggestion.key,
          ),
        });
      }
      return { previous };
    },
    onError: (err, _s, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
      notifyError(err, {
        fallbackCode: "SAVE_FAILED",
        actionLabel: "dismiss suggestion",
      });
    },
    onSuccess: (body) => {
      genreUndoToast(body.label, () => undo.mutate());
    },
    onSettled: (_data, _err, input) => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ["undo"] });
      // Global block can affect every open detail cache.
      if (input.scope === "global") {
        void qc.invalidateQueries({ queryKey: ["entity-detail"] });
      }
    },
  });
}
